import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Category } from '@shared/types/product';
import { useCategories } from '@/lib/hooks/useCategories';
import { CategoriesContextShape } from './types';

const initialState = {} as CategoriesContextShape;
export const CategoriesContext = createContext(initialState);

const CategoriesProvider = ({
  children,
  businessUnitKey,
  storeKey,
}: {
  children: React.ReactNode;
  businessUnitKey: string | undefined;
  storeKey: string | undefined;
}) => {
  const categoryOptions = useMemo(() => {
    return businessUnitKey
      ? {
          businessUnitKey,
          storeKey,
          format: 'tree',
          limit: 500,
        }
      : undefined;
  }, [businessUnitKey, storeKey]);

  const { categories: fetchedCategories } = useCategories(categoryOptions);

  const [categories, setCategories] = useState<Category[]>(fetchedCategories);
  const [isLoading, setIsLoadingCategories] = useState(false);

  useEffect(() => {
    if (!businessUnitKey || !storeKey || !fetchedCategories?.length) return;
    if (fetchedCategories !== categories) {
      setIsLoadingCategories(true);
      setCategories(fetchedCategories);
      setIsLoadingCategories(false);
    }
  }, [businessUnitKey, storeKey, fetchedCategories, categories]);

  const value = useMemo(
    () => ({
      categories,
      isLoading,
    }),
    [categories, isLoading],
  );

  return <CategoriesContext.Provider value={value}>{children}</CategoriesContext.Provider>;
};

export const useCategoriesByBUContext = () => {
  const context = useContext(CategoriesContext);
  if (!context) {
    throw new Error('useCategoriesContext must be used within a CategoriesProvider');
  }
  return context;
};

export default CategoriesProvider;
